import React, { useEffect, useState, useCallback } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import { hosysAiApi } from "@/api/hosysai";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import { toast } from "sonner";
import { Plus, AlertTriangle, Loader2, X, Check, Trash2 } from "lucide-react";

const SEVERITY_STYLE = {
  low:      { badge: "bg-slate-500/10 text-slate-300 border-slate-500/30", label: "Baixa" },
  medium:   { badge: "bg-amber-500/10 text-amber-300 border-amber-500/30", label: "Mitjana" },
  high:     { badge: "bg-orange-500/10 text-orange-300 border-orange-500/30", label: "Alta" },
  critical: { badge: "bg-red-500/10 text-red-300 border-red-500/30", label: "Crítica" },
};

const STATUS_STYLE = {
  open:        { badge: "bg-red-500/10 text-red-300", label: "Oberta" },
  in_progress: { badge: "bg-amber-500/10 text-amber-300", label: "En curs" },
  resolved:    { badge: "bg-emerald-500/10 text-emerald-300", label: "Resolta" },
  closed:      { badge: "bg-slate-500/10 text-slate-400", label: "Tancada" },
};

export default function HosysAIIncidents() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [meta, setMeta] = useState(null);
  const [systems, setSystems] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    incident_type: "other", ai_system_id: "", ai_system_name: "",
    affected_user: "", severity: "medium", description: "", action_taken: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const [m, l, sys] = await Promise.all([
        hosysAiApi.meta(),
        hosysAiApi.listIncidents(venueId),
        hosysAiApi.listSystems(venueId),
      ]);
      setMeta(m); setItems(l || []); setSystems(sys || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("La descripció és obligatòria"); return; }
    setSaving(true);
    try {
      const chosenSystem = systems.find((s) => s.ai_system_id === form.ai_system_id);
      await hosysAiApi.createIncident({
        venue_id: venueId,
        ...form,
        ai_system_name: chosenSystem?.name || form.ai_system_name || null,
      });
      toast.success("Incidència registrada");
      setShowForm(false);
      setForm({ date: new Date().toISOString().slice(0, 10), incident_type: "other", ai_system_id: "", ai_system_name: "", affected_user: "", severity: "medium", description: "", action_taken: "" });
      load();
    } catch (e) { toast.error(e.message || "Error"); } finally { setSaving(false); }
  };

  const changeStatus = async (inc, status) => {
    try {
      await hosysAiApi.updateIncident(inc.incident_id, { status });
      toast.success("Actualitzat");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar incidència?")) return;
    try {
      await hosysAiApi.deleteIncident(id);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const typeLabel = (code) => meta?.incident_types.find((t) => t.code === code)?.label || code;

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-incidents-page">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Incidències</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Registre d'incidències IA</h1>
            <p className="text-slate-400 text-sm mt-1">Documenta errors, filtracions o problemes amb sistemes d'IA per traçabilitat i millora contínua.</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold flex items-center gap-2 text-sm"
            data-testid="incidents-add-btn"
          >
            <Plus className="w-4 h-4" /> Nova incidència
          </button>
        </div>

        {loading || venueLoading ? (
          <div className="h-40 flex items-center justify-center text-slate-400">Carregant…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-white/10 bg-white/5">
            <AlertTriangle className="w-12 h-12 mx-auto text-slate-500 mb-3" />
            <p className="text-white font-semibold">Cap incidència registrada</p>
            <p className="text-sm text-slate-400 mt-1">Perfecte! Si en detectes alguna, registra-la aquí.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((inc) => {
              const sev = SEVERITY_STYLE[inc.severity] || SEVERITY_STYLE.medium;
              const st = STATUS_STYLE[inc.status] || STATUS_STYLE.open;
              return (
                <div key={inc.incident_id} className="p-4 rounded-2xl border border-white/10 bg-white/5" data-testid={`incident-${inc.incident_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${sev.badge}`}>{sev.label}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full ${st.badge}`}>{st.label}</span>
                        <span className="text-xs text-slate-400">{typeLabel(inc.incident_type)}</span>
                        <span className="text-xs text-slate-500">· {inc.date}</span>
                      </div>
                      <p className="text-sm text-white">{inc.description}</p>
                      {inc.ai_system_name && <p className="text-xs text-slate-400 mt-1">Sistema: {inc.ai_system_name}</p>}
                      {inc.action_taken && <p className="text-xs text-slate-300 mt-1"><span className="text-slate-500">Acció: </span>{inc.action_taken}</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                      {inc.status !== "resolved" && inc.status !== "closed" && (
                        <button onClick={() => changeStatus(inc, "resolved")} className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10" title="Marcar com resolta">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => remove(inc.incident_id)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal */}
        {showForm && meta && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center justify-between">
                <p className="font-semibold text-white">Nova incidència</p>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-white/10">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Data</label>
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Gravetat</label>
                    <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                      className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100">
                      <option value="low">Baixa</option>
                      <option value="medium">Mitjana</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Tipus</label>
                  <select value={form.incident_type} onChange={(e) => setForm({ ...form, incident_type: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100">
                    {meta.incident_types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Sistema IA relacionat</label>
                  <select value={form.ai_system_id} onChange={(e) => setForm({ ...form, ai_system_id: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100">
                    <option value="">— Cap / Altre —</option>
                    {systems.map((s) => <option key={s.ai_system_id} value={s.ai_system_id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Descripció *</label>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400"
                    placeholder="Descriu detalladament què ha passat" data-testid="incident-description" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Acció presa</label>
                  <textarea rows={2} value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100"
                    placeholder="Què s'ha fet per resoldre-ho?" />
                </div>
              </div>
              <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-white/10 px-5 py-3 flex gap-2 justify-end">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel·lar</button>
                <button onClick={submit} disabled={saving}
                  className="px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="incident-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HosysAILayout>
  );
}
